import {Component, OnDestroy, OnInit} from '@angular/core';
import {Subscription} from 'rxjs';

import {AutenticacaoService} from "../../../../shared/clients/user-client";
import {SelecaoLojasService} from "../../../../shared/helpers/selecao-lojas.service";

interface Loja {
    cnpj: string;
    nome: string;
    /** "Cidade · Bairro" — vem embutido no nome_fantasia devolvido pela API. */
    local: string;
}

interface Grupo {
    slug: string;
    nome: string;
    lojas: Loja[];
    /** Loja sem grupo econômico: vira um "grupo" de uma loja só, exibido compacto. */
    avulsa: boolean;
}

interface ItemLista {
    grupo: Grupo;
    lojasVisiveis: Loja[];
    selecionado: boolean;
    parcial: boolean;
    expandido: boolean;
}

// Busca tolerante: sem acento, espaço, pontuação e caixa. "Super Pão" casa "superpao".
const normalizarBusca = (texto: string): string =>
    (texto || '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');

// Só os dígitos — casa CNPJ com ou sem máscara.
const apenasDigitos = (texto: string): string => (texto || '').replace(/\D/g, '');

@Component({
    selector: 'app-navbar-credenciado',
    styleUrls: ['./navbar-credenciado.component.scss'],
    templateUrl: './navbar-credenciado.component.html'
})
export class NavbarCredenciadoComponent implements OnInit, OnDestroy {
    grupos: Grupo[] = [];
    totalLojas = 0;
    selecionados = new Set<string>();

    termoBusca = '';
    expandidos = new Set<string>();

    carregando = true;
    erro = false;

    // Derivados — recalculados a cada mudança (nada de função pesada no template).
    lista: ItemLista[] = [];
    qtdGruposSelecionados = 0;
    rotuloGatilho = '';

    private inscricaoSelecao?: Subscription;

    constructor(
        private autenticacaoService: AutenticacaoService,
        private selecaoLojas: SelecaoLojasService,
    ) {
    }

    ngOnInit(): void {
        this.lerSelecaoSalva();
        this.carregar();

        // A tela (campo "Credenciado") ou outra aba mudou a seleção: o header acompanha.
        this.inscricaoSelecao = this.selecaoLojas.mudancas$.subscribe(() => {
            this.lerSelecaoSalva();
            this.recalcular();
        });
    }

    ngOnDestroy(): void {
        this.inscricaoSelecao?.unsubscribe();
    }

    carregar(): void {
        this.carregando = true;
        this.erro = false;
        this.recalcular();

        this.autenticacaoService.obterCredenciados().subscribe({
            next: data => {
                this.montarGrupos(data?.dados || []);
                this.carregando = false;

                if (this.totalLojas === 1) {
                    this.selecionarTodos();
                } else {
                    this.recalcular();
                }
            },
            error: () => {
                this.carregando = false;
                this.erro = true;
                this.recalcular();
            }
        });
    }

    // Ao abrir, relê a seleção: algumas telas também gravam o CNPJ direto no storage.
    aoAbrir(aberto: boolean): void {
        if (aberto) {
            this.lerSelecaoSalva();
            this.recalcular();
        }
    }

    aoBuscar(termo: string): void {
        this.termoBusca = termo;
        // Com busca ativa, abre os grupos encontrados (senão a pessoa não vê as lojas).
        this.expandidos = this.buscando()
            ? new Set(this.grupos.filter(g => this.grupoCasaBusca(g)).map(g => g.slug))
            : new Set();
        this.recalcular();
    }

    toggleGrupo(grupo: Grupo): void {
        const cnpjs = grupo.lojas.map(l => l.cnpj);
        const todas = cnpjs.every(c => this.selecionados.has(c));
        cnpjs.forEach(c => todas ? this.selecionados.delete(c) : this.selecionados.add(c));
        this.persistir();
    }

    toggleLoja(loja: Loja): void {
        if (this.selecionados.has(loja.cnpj)) {
            this.selecionados.delete(loja.cnpj);
        } else {
            this.selecionados.add(loja.cnpj);
        }
        this.persistir();
    }

    toggleExpandir(grupo: Grupo, evento?: Event): void {
        evento?.stopPropagation();
        evento?.preventDefault();
        if (this.expandidos.has(grupo.slug)) {
            this.expandidos.delete(grupo.slug);
        } else {
            this.expandidos.add(grupo.slug);
        }
        this.recalcular();
    }

    selecionarTodos(): void {
        this.grupos.forEach(g => g.lojas.forEach(l => this.selecionados.add(l.cnpj)));
        this.persistir();
    }

    limpar(): void {
        this.selecionados.clear();
        this.persistir();
    }

    trackGrupo(_: number, item: ItemLista): string {
        return item.grupo.slug;
    }

    trackLoja(_: number, loja: Loja): string {
        return loja.cnpj;
    }

    private montarGrupos(credenciados: any[]): void {
        const porSlug = new Map<string, Grupo>();
        const grupos: Grupo[] = [];

        credenciados.forEach(c => {
            if (!c?.cnpj) {
                return;
            }
            const loja = this.paraLoja(c);
            const slug = c.grupo_economico_slug;

            if (!slug) {
                grupos.push({slug: 'avulsa-' + loja.cnpj, nome: loja.nome, lojas: [loja], avulsa: true});
                return;
            }

            let grupo = porSlug.get(slug);
            if (!grupo) {
                grupo = {slug, nome: c.grupo_economico_nome || slug, lojas: [], avulsa: false};
                porSlug.set(slug, grupo);
                grupos.push(grupo);
            }
            grupo.lojas.push(loja);
        });

        this.grupos = grupos;
        this.totalLojas = grupos.reduce((total, g) => total + g.lojas.length, 0);
    }

    // A API devolve nome_fantasia como "NOME - Cidade - Bairro"; separa o nome do local.
    private paraLoja(c: any): Loja {
        const partes = String(c.nome_fantasia || '').split(' - ');
        if (partes.length < 3) {
            return {cnpj: c.cnpj, nome: partes.join(' - '), local: ''};
        }
        const local = partes.slice(-2).filter(p => p && p !== 'S/Cidade' && p !== 'S/Bairro');
        return {cnpj: c.cnpj, nome: partes.slice(0, -2).join(' - '), local: local.join(' · ')};
    }

    private lojaCasaBusca(loja: Loja): boolean {
        const termo = normalizarBusca(this.termoBusca);
        const digitos = apenasDigitos(this.termoBusca);
        return (termo !== '' && (normalizarBusca(loja.nome).includes(termo) || normalizarBusca(loja.local).includes(termo)))
            || (digitos !== '' && apenasDigitos(loja.cnpj).includes(digitos));
    }

    private grupoCasaBusca(grupo: Grupo): boolean {
        const termo = normalizarBusca(this.termoBusca);
        return (termo !== '' && !grupo.avulsa && normalizarBusca(grupo.nome).includes(termo))
            || grupo.lojas.some(l => this.lojaCasaBusca(l));
    }

    // Busca pelo nome do grupo traz o grupo inteiro; senão, só as lojas que casam.
    private lojasVisiveis(grupo: Grupo): Loja[] {
        if (!this.buscando()) {
            return grupo.lojas;
        }
        if (!grupo.avulsa && normalizarBusca(grupo.nome).includes(normalizarBusca(this.termoBusca))) {
            return grupo.lojas;
        }
        return grupo.lojas.filter(l => this.lojaCasaBusca(l));
    }

    private buscando(): boolean {
        return normalizarBusca(this.termoBusca) !== '' || apenasDigitos(this.termoBusca) !== '';
    }

    private recalcular(): void {
        const buscando = this.buscando();
        const selecionados: ItemLista[] = [];
        const restantes: ItemLista[] = [];

        this.grupos.forEach(grupo => {
            if (buscando && !this.grupoCasaBusca(grupo)) {
                return;
            }
            const qtd = grupo.lojas.filter(l => this.selecionados.has(l.cnpj)).length;
            const item: ItemLista = {
                grupo,
                lojasVisiveis: this.lojasVisiveis(grupo),
                selecionado: qtd > 0 && qtd === grupo.lojas.length,
                parcial: qtd > 0 && qtd < grupo.lojas.length,
                expandido: this.expandidos.has(grupo.slug),
            };
            // Grupos com alguma loja marcada sobem para o topo.
            (qtd > 0 ? selecionados : restantes).push(item);
        });

        this.lista = [...selecionados, ...restantes];
        this.qtdGruposSelecionados = selecionados.length;

        const qtd = this.selecionados.size;
        if (qtd === 0) {
            this.rotuloGatilho = 'Selecione lojas/grupos';
        } else if (this.totalLojas > 0 && qtd === this.totalLojas) {
            this.rotuloGatilho = 'Todas as lojas';
        } else {
            this.rotuloGatilho = `${qtd} ${qtd === 1 ? 'loja' : 'lojas'}`;
        }
    }

    private lerSelecaoSalva(): void {
        this.selecionados = new Set(this.selecaoLojas.obter());
    }

    private persistir(): void {
        this.selecaoLojas.definir(Array.from(this.selecionados));
        this.recalcular();
    }
}
